import { MouseEventHandler, useEffect, useRef, useState } from "react"
import { Alert, Col, Form, InputGroup, Modal, Row } from "react-bootstrap"
import styles from "./Login.module.css"
import DefaultButton from "../../components/buttons/default-button/DefaultButton"
import { gql } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import MainButton from "../../components/buttons/nav-button/MainButton";

const LDAP_LOGIN_QUERY = gql`
    query Query($username: String!, $password: String!) {
        ldapLogin(username: $username, password: $password) {
            success
            errorMessage
            userId
        }
    }
`;

const CHECK_TOKEN_VALIDITY = gql`
    query CheckTokenValidity($token: String!) {
        checkTokenValidity(token: $token) {
            isValid
        }
}
`;
const ENABLE_VPN = gql`
    mutation Mutation($vpnUserId: Int!, $totpCode: String!) {
        enableVPN(vpnUserId: $vpnUserId, totpCode: $totpCode) {
            success
            token
            errorMessage
        }
    }
`;

interface ldapLoginResult {
    ldapLogin: {
        success: boolean
        errorMessage?: string
        userId: number
    }
}

interface totpCodeResult {
    enableVPN: {
        success: boolean
        token: string
        errorMessage?: string
    }
}

interface checkTokenValidity {
    checkTokenValidity: {
        isValid: boolean
    }
}

const DISABLE_VPN_QUERY = gql`
    mutation DisableVPN($token: String!) {
        disableVPN(token: $token) {
            success
            errorMessage
        }
    }
`

interface disableVPNResult {
    disableVPN: {
        success: boolean
        errorMessage?: string
    }
}

export default function StepSliderModal() {
    const stepsCount = 3
    const [ step, setStep ] = useState(0)
    const [ ldapValidated, setLdapValidated ] = useState(false)
    const [ totpValidated, setTotpValidated ] = useState(false)
    const [ errorMessage, setErrorMessage ] = useState("")
    const [ username, setUsername ] = useState("")
    const [ password, setPassword ] = useState("")
    const [ isError, setIsError ] = useState(false)
    const [ vpnUserId, setvpnUserId ] = useState(0)
    const [ totpCode, setTotpCode ] = useState("")
    const [ peerToken, setPeerToken ] = useState("")
    const client = useApolloClient()

    const userNameRef = useRef<HTMLInputElement>(null)
    const totpCodeRef = useRef<HTMLInputElement>(null)

    const nextStep = () => {
        setStep((prev) => (prev < stepsCount - 1 ? prev + 1 : prev))
    }

    const prevStep = () => {
        clearError()
        setStep((prev) => (prev > 0 ? prev - 1 : prev))
    }

    const handleTOTPCode: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault()

        setTotpValidated(true)

        if(totpCode.length === 0) {
            setIsError(true)
            setErrorMessage("TOTP kód megadása kötelező")
            return
        }

        if(totpCode.length !== 6 || /^\d+$/.test(totpCode) === false) {
            setIsError(true)
            setErrorMessage("Hibás TOTP kód<br />A kódnak 6 számból kell állnia")
            return
        }

        try {
            const data = await client.mutate<totpCodeResult>({
                mutation: ENABLE_VPN,
                variables: { vpnUserId, totpCode },
                fetchPolicy: 'no-cache'
            })

            if(!data || !data.data) {
                setIsError(true)
                setErrorMessage("Hiba a kód ellenőrzése közben")
                return
            }

            const result = data.data.enableVPN

            if(result.success) {
                nextStep()
                localStorage.setItem(process.env.REACT_APP_ACTIVE_VPN_CHECK_TOKEN, result.token)
                setPeerToken(result.token)
            }
            else {
                const errorMessage = result.errorMessage ?? "Hibás TOTP kód! Kérem próbálja újra!"
                setIsError(true)
                setErrorMessage(errorMessage)
            }
            
        } catch (error) {
            console.log(error)
        }
    }

    const clearError = () => {
        setIsError(false)
        setErrorMessage("")
    }

    const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault()
        clearError()

        setLdapValidated(true)

        if(username.length === 0 && password.length === 0) {
            setIsError(true)
            setErrorMessage("Felhasználónév megadása kötelező<br />Jelszó megadása kötelező")
            return
        }

        try {
            const data = await client.query<ldapLoginResult>({
                query: LDAP_LOGIN_QUERY,
                variables: { username, password },
                fetchPolicy: 'no-cache'
            })

            if(!data || !data.data) {
                setIsError(true)
                setErrorMessage("Hiba a bejelentkezés közben")
                return
            }

            const result = data.data.ldapLogin
            if(result.success) {
                setvpnUserId(data.data.ldapLogin.userId)
                nextStep()
            }
            else {
                const errorMessage = result.errorMessage ?? "Hiba a bejelentkezés közben"
                setIsError(true)
                setErrorMessage(errorMessage)
            }
        }
        catch {
            setIsError(true)
            setErrorMessage("Hiba a bejelentkezés közben")
        }
    }

    const resetScreen = () => {
        setStep(0)
        setLdapValidated(false)
        setTotpValidated(false)
        setErrorMessage("")
        setUsername("")
        setPassword("")
        setIsError(false)
        setvpnUserId(0)
        setTotpCode("")
        setPeerToken("")

        localStorage.removeItem(process.env.REACT_APP_ACTIVE_VPN_CHECK_TOKEN)
    }

    const disablePeer: MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault()

        if(peerToken === "") {
            resetScreen()
            return
        }

        try {
            const data = await client.mutate<disableVPNResult>({
                mutation: DISABLE_VPN_QUERY,
                variables: { token: peerToken },
                fetchPolicy: 'no-cache'
            })

            if(data.data) {
                const { success, errorMessage } = data.data.disableVPN
                if(!success) {
                    console.log(errorMessage)
                }
            }
        } catch (error) {
            console.log(error)
        } finally {
            resetScreen()
        }
    }

    useEffect(() => {
        const token = localStorage.getItem(process.env.REACT_APP_ACTIVE_VPN_CHECK_TOKEN);
        if (token) {
            const checkTokenValidity = async (token: string) => {
                try {
                    const data = await client.query<checkTokenValidity>({
                        query: CHECK_TOKEN_VALIDITY,
                        variables: { token },
                        fetchPolicy: 'no-cache'
                    })

                    if(data.data?.checkTokenValidity.isValid) {
                        setStep(2)
                        setPeerToken(token)
                    }
                    else {
                        localStorage.removeItem(process.env.REACT_APP_ACTIVE_VPN_CHECK_TOKEN)
                    }

                } catch (error) {
                    console.log(error)
                }
            }
            checkTokenValidity(token)
        }
    }, [ client ]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if(step === 0) {
                const elem = userNameRef.current
                if(elem) {
                    elem.focus()
                }
            }
            else if(step === 1) {
                const elem = totpCodeRef.current
                if(elem) {
                    elem.focus()
                }
            }
        }, 500) // the duration of the animation is 0.4s so we add +100 ms

        return(() => {
            clearTimeout(timeout)
        })
    }, [ step, userNameRef, totpCodeRef ])
    
    return (
        <Modal show centered className={ styles.container }>
            <Modal.Header style={{justifyContent: "center"}}>
                <Modal.Title>VPN hozzáférés megnyitása</Modal.Title>
            </Modal.Header>

            <Modal.Body className={styles.modalBody}>
                <div className={styles.sliderWrapper}>
                    <div
                        className={styles.sliderInner}
                        style={{ transform: `translateX(-${step * 100}%)` }}
                    >
                        {/* SLIDE 0 */}
                        <div className={styles.slide}>
                            <Form id="ldap-login" noValidate validated={ ldapValidated } onSubmit={ handleSubmit } >
                                <Row className='mb-3 justify-content-center'>
                                    <Form.Group as={ Col } md="10" controlId="username">
                                        <InputGroup hasValidation>
                                            <Form.Control
                                                type="text"
                                                placeholder="Felhasználónév"
                                                required
                                                value={ username }
                                                onChange={ e => setUsername(e.target.value) }
                                                autoFocus={ true }
                                                ref={ userNameRef }
                                            />

                                            <Form.Control.Feedback type="invalid">
                                                Felhasználónév megadása kötelező
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                </Row>

                                <Row className='mb-3 justify-content-center'>
                                    <Form.Group as={ Col } md="10" controlId="password">
                                        <InputGroup hasValidation>
                                            <Form.Control
                                                type="password"
                                                placeholder="Jelszó"
                                                value={ password }
                                                required
                                                onChange={ e => setPassword(e.target.value) }
                                            />

                                            <Form.Control.Feedback type="invalid">
                                                Jelszó megadása kötelező
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                </Row>
                                {isError && (
                                    <Row className="justify-content-center">
                                        <Col md="10">
                                            <Alert
                                                variant="danger"
                                                onClose={() => setIsError(false)}
                                                dismissible
                                            >
                                                <b><span dangerouslySetInnerHTML={{ __html: errorMessage }} /></b>
                                            </Alert>
                                        </Col>
                                    </Row>
                                )}
                            </Form>
                        </div>

                        {/* SLIDE 1 */}
                        <div className={styles.slide}>
                            <Form id="totp-code" noValidate validated={ totpValidated } onSubmit={ handleTOTPCode } >
                                <Row className='mb-3 justify-content-center'>
                                    <Form.Group as={ Col } md="10" controlId="totp-code">
                                        <InputGroup hasValidation>
                                            <Form.Control
                                                type="text"
                                                placeholder="TOTP kód"
                                                required
                                                value={ totpCode }
                                                onChange={ e => setTotpCode(e.target.value) }
                                                minLength={6}
                                                maxLength={6}
                                                ref={ totpCodeRef }
                                            />

                                            <Form.Control.Feedback type="invalid">
                                                TOTP kód megadása kötelező!
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                </Row>
                                {isError && (
                                    <Row className="justify-content-center">
                                        <Col md="10">
                                            <Alert
                                                variant="danger"
                                                onClose={() => setIsError(false)}
                                                dismissible
                                            >
                                                <b><span dangerouslySetInnerHTML={{ __html: errorMessage }} /></b>
                                            </Alert>
                                        </Col>
                                    </Row>
                                )}
                            </Form>
                        </div>

                        {/* SLIDE 2 */}
                        <div className={styles.slide}>
                            {
                                step === 2 &&
                                <>
                                    {/* IDE JÖN A HARMADIK LÉPÉS TARTALMA */}
                                    <div className={styles.successWrap} key={step}>
                                        <div className={styles.successText}>
                                            Sikeres validáció!<br />
                                            VPN kapcsolat létrehozható!
                                        </div>

                                        <svg className={styles.checkSvg} viewBox="0 0 120 120">
                                            <circle className={styles.checkCircle} cx="60" cy="60" r="54" />
                                            <path className={styles.checkPath} d="M34 62 L52 78 L88 42" />
                                        </svg>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className={ styles.modalFooter }>
                {
                    step === 0 && (
                        <Row className="align-items-center">
                            <Col sm="12" className="text-end">
                                <DefaultButton title={"Belépés"} form="ldap-login" />
                            </Col>
                        </Row>
                    )
                }

                {
                    step === 1 && (
                        <Row className="align-items-center">
                            <Col sm="6" className="text-start">
                                <MainButton title={"Vissza"} onClick={() => {
                                    prevStep()
                                    setTotpCode("")
                                    setvpnUserId(0)
                                    setTotpValidated(false)
                                }} active={false} />
                            </Col>

                            <Col sm="6" className="text-end">
                                <DefaultButton title={"Validálás"} form="totp-code" />
                            </Col>
                        </Row>
                    )
                }

                {
                    step === 2 && (
                        <Row className="align-items-center">
                            <Col sm="12" className="text-center">
                                <MainButton title={"VPN lekapcsolása"} onClick={ disablePeer } active={ false } />
                            </Col>
                        </Row>
                    )
                }
            </Modal.Footer>
        </Modal>
    )
}